import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import * as customerVesselService from '../firebase/services/customerVesselService';
import { Customer, CustomerVessel } from '../firebase/services/customerVesselService';
import { useAuth } from './AuthContext';
import { getUserRoleId } from '../utils/auth';
import { getRoleById } from '../firebase/services/roleService';
import { Role } from '../types/rbac';

interface CustomerVesselContextType {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  searchCustomers: (searchTerm: string) => Promise<Customer[]>;
  getCustomerById: (customerId: string) => Promise<Customer | null>;
  createCustomerVessel: (customerId: string, vesselData: Omit<CustomerVessel, 'vesselId' | 'addedAt' | 'status'>) => Promise<string>;
  updateCustomerVessel: (customerId: string, vesselId: string, vesselData: Partial<CustomerVessel>) => Promise<void>;
  deleteCustomerVessel: (customerId: string, vesselId: string) => Promise<void>;
  searchVesselsByRegistration: (registrationNumber: string) => Promise<{vessel: CustomerVessel, customerId: string, customer: Customer}[]>;
  searchVesselsByName: (name: string) => Promise<{vessel: CustomerVessel, customerId: string, customer: Customer}[]>;
  getAllCustomerVessels: () => Promise<{vessel: CustomerVessel, customerId: string, customer: Customer}[]>;
  addLaunchActivity: (vesselId: string, activityData: any) => Promise<string>;
  getVesselLaunchActivity: (vesselId: string) => Promise<any[]>;
  getTodayLaunchActivity: () => Promise<any[]>;
  refreshCustomers: () => Promise<void>;
}

const CustomerVesselContext = createContext<CustomerVesselContextType | undefined>(undefined);

export const useCustomerVessels = (): CustomerVesselContextType => {
  const context = useContext(CustomerVesselContext);
  if (!context) {
    throw new Error('useCustomerVessels must be used within a CustomerVesselProvider');
  }
  return context;
};

interface CustomerVesselProviderProps {
  children: ReactNode;
}

export const CustomerVesselProvider: React.FC<CustomerVesselProviderProps> = ({ children }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { userProfile } = useAuth();

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Only attempt to load customers if user is authenticated and has staff/admin role
      if (userProfile && userProfile.id) {
        // Check if roleId exists
        if (!userProfile.roleId) {
          setCustomers([]);
          setError('Access restricted: User role not properly configured');
          return;
        }

        const roleDoc = await getRoleById(userProfile.roleId);
        if (roleDoc && ['staff', 'admin'].includes(roleDoc.name)) {
          const allCustomers = await customerVesselService.getAllCustomers();
          setCustomers(allCustomers);
        } else {
          setCustomers([]);
        }
      } else {
        // If user doesn't have proper permissions, set empty customers array
        setCustomers([]);
        // Only set error if user is authenticated (to avoid showing error during initial load)
        if (userProfile && userProfile.id) {
          setError('Access restricted: You need staff or admin privileges to view customer data');
        }
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setCustomers([]); // Set empty array on error
      
      // Check if this is a permission error
      if (err instanceof Error && err.toString().includes('Missing or insufficient permissions')) {
        setError('Access restricted: You may not have permission to view customers data');
      } else {
        setError('Failed to load customers');
      }
    } finally {
      setLoading(false);
    }
  }, [userProfile]); // Use userProfile instead of userId, userProfile

  // Add listener for auth state changes
  useEffect(() => {
    const handleAuthChange = () => {
      console.log('🔄 CustomerVesselContext: Auth state changed, reloading customers');
      // Reload customers when auth state changes - use userProfile.id instead of userId
      if (userProfile && userProfile.id) {
        loadCustomers();
      }
    };
    
    window.addEventListener('authStateChange', handleAuthChange);
    
    return () => {
      window.removeEventListener('authStateChange', handleAuthChange);
    };
  }, [userProfile, loadCustomers]); // Use userProfile instead of userId

  // Force reload when userProfile changes (after re-authentication)
  useEffect(() => {
    console.log('🔄 CustomerVesselContext: userProfile changed, checking if we need to reload');
    if (userProfile && userProfile.id && userProfile.roleId) {
      console.log('🔄 CustomerVesselContext: Triggering immediate customer reload after userProfile change');
      loadCustomers();
    }
  }, [userProfile, loadCustomers]);

  useEffect(() => {
    console.log('🔄 CustomerVesselContext: Main useEffect triggered', { hasUserProfile: !!userProfile, roleId: userProfile?.roleId, userProfileId: userProfile?.id });
    
    // Only load customers when user profile is available - use userProfile.id instead of userId
    if (userProfile && userProfile.id) {
      const checkRolesAndLoad = async () => {
        try {
          console.log('🔍 CustomerVesselContext: Checking roles and loading customers');
          setLoading(true);
          setError(null);
          
          // Check if roleId exists in userProfile
          if (!userProfile.roleId) {
            setCustomers([]);
            setLoading(false);
            setError('Access restricted: User role not properly configured');
            return;
          }

          // Get role from userProfile
          const roleDoc = await getRoleById(userProfile.roleId);
          const userRoleName = roleDoc?.name || '';

          console.log('🎭 CustomerVesselContext: User role:', userRoleName);

          // Check if user has required role
          if (["staff", "admin"].includes(userRoleName)) {
            console.log('✅ CustomerVesselContext: User has proper role, loading customers');
            
            // Inline the customer loading logic here
            const allCustomers = await customerVesselService.getAllCustomers();
            console.log('📋 CustomerVesselContext: Loaded customers:', allCustomers.length);
            setCustomers(allCustomers);
            setLoading(false);
          } else {
            // Reset state when user doesn't have required role
            console.log('❌ CustomerVesselContext: User lacks proper role');
            setCustomers([]);
            setLoading(false);
            setError('Access restricted: You need staff or admin privileges to view customer data');
          }
        } catch (err) {
          console.error('❌ CustomerVesselContext: Error checking roles or loading customers:', err);
          setCustomers([]);
          setLoading(false);
          setError('Failed to verify user permissions or load customers');
        }
      };
      checkRolesAndLoad();
    } else {
      // Reset state when no user is logged in
      console.log('🚫 CustomerVesselContext: No user or profile, resetting state');
      setCustomers([]);
      setLoading(false);
      setError(null);
    }
  }, [userProfile]); // Only depend on userProfile, not userId

  const refreshCustomers = async () => {
    await loadCustomers();
  };

  const searchCustomers = async (searchTerm: string) => {
    try {
      // Only attempt to search customers if user is authenticated and has staff/admin role
      if (userProfile && userProfile.id) {
        const roleDoc = await getRoleById(userProfile.roleId);
        if (roleDoc && ['staff', 'admin'].includes(roleDoc.name)) {
          return await customerVesselService.searchCustomers(searchTerm);
        } else {
          return [];
        }
      } else {
        if (userProfile && userProfile.id) {
          setError('Access restricted: You need staff or admin privileges to search customers');
        }
        return [];
      }
    } catch (err) {
      console.error('Error searching customers:', err);
      
      // Check if this is a permission error
      if (err instanceof Error && err.toString().includes('Missing or insufficient permissions')) {
        setError('Access restricted: You may not have permission to search customers');
      } else {
        setError('Failed to search customers');
      }
      return [];
    }
  };

  const getCustomerById = async (customerId: string) => {
    try {
      return await customerVesselService.getCustomerById(customerId);
    } catch (err) {
      console.error('Error getting customer by ID:', err);
      
      // Check if this is a permission error
      if (err instanceof Error && err.toString().includes('Missing or insufficient permissions')) {
        setError('Access restricted: You may not have permission to view customer details');
      } else {
        setError('Failed to get customer');
      }
      return null;
    }
  };

  const createCustomerVessel = async (
    customerId: string,
    vesselData: Omit<CustomerVessel, 'vesselId' | 'addedAt' | 'status'>
  ) => {
    try {
      const vesselId = await customerVesselService.createCustomerVessel(customerId, vesselData);
      await refreshCustomers(); // Refresh the customers list after creating a vessel
      return vesselId;
    } catch (err) {
      console.error('Error creating customer vessel:', err);
      setError('Failed to create vessel');
      throw err;
    }
  };

  const updateCustomerVessel = async (
    customerId: string,
    vesselId: string,
    vesselData: Partial<CustomerVessel>
  ) => {
    try {
      await customerVesselService.updateCustomerVessel(customerId, vesselId, vesselData);
      await refreshCustomers(); // Refresh the customers list after updating a vessel
    } catch (err) {
      console.error('Error updating customer vessel:', err);
      setError('Failed to update vessel');
      throw err;
    }
  };

  const deleteCustomerVessel = async (customerId: string, vesselId: string) => {
    try {
      await customerVesselService.deleteCustomerVessel(customerId, vesselId);
      await refreshCustomers(); // Refresh the customers list after deleting a vessel
    } catch (err) {
      console.error('Error deleting customer vessel:', err);
      setError('Failed to delete vessel');
      throw err;
    }
  };

  const searchVesselsByRegistration = async (registrationNumber: string) => {
    try {
      // Only attempt to search vessels if user is authenticated and has staff/admin role
      if (userProfile && userProfile.id) {
        const roleDoc = await getRoleById(userProfile.roleId);
        if (roleDoc && ['staff', 'admin'].includes(roleDoc.name)) {
          return await customerVesselService.searchVesselsByRegistration(registrationNumber);
        } else {
          return [];
        }
      } else {
        if (userProfile && userProfile.id) {
          setError('Access restricted: You need staff or admin privileges to search vessels');
        }
        return [];
      }
    } catch (err) {
      console.error('Error searching vessels by registration:', err);
      
      // Check if this is a permission error
      if (err instanceof Error && err.toString().includes('Missing or insufficient permissions')) {
        setError('Access restricted: You may not have permission to search vessels');
      } else {
        setError('Failed to search vessels');
      }
      return [];
    }
  };

  const searchVesselsByName = async (name: string) => {
    try {
      // Only attempt to search vessels if user is authenticated and has staff/admin role
      if (userProfile && userProfile.id) {
        const roleDoc = await getRoleById(userProfile.roleId);
        if (roleDoc && ['staff', 'admin'].includes(roleDoc.name)) {
          return await customerVesselService.searchVesselsByName(name);
        } else {
          return [];
        }
      } else {
        if (userProfile && userProfile.id) {
          setError('Access restricted: You need staff or admin privileges to search vessels');
        }
        return [];
      }
    } catch (err) {
      console.error('Error searching vessels by name:', err);
      
      // Check if this is a permission error
      if (err instanceof Error && err.toString().includes('Missing or insufficient permissions')) {
        setError('Access restricted: You may not have permission to search vessels');
      } else {
        setError('Failed to search vessels');
      }
      return [];
    }
  };

  const getAllCustomerVessels = async () => {
    try {
      // Only attempt to get all vessels if user is authenticated and has staff/admin role
      if (userProfile && userProfile.id) {
        const roleDoc = await getRoleById(userProfile.roleId);
        if (roleDoc && ['staff', 'admin'].includes(roleDoc.name)) {
          return await customerVesselService.getAllCustomerVessels();
        } else {
          return [];
        }
      } else {
        if (userProfile && userProfile.id) {
          setError('Access restricted: You need staff or admin privileges to view vessels');
        }
        return [];
      }
    } catch (err) {
      console.error('Error getting all customer vessels:', err);
      
      // Check if this is a permission error
      if (err instanceof Error && err.toString().includes('Missing or insufficient permissions')) {
        setError('Access restricted: You may not have permission to view vessels');
      } else {
        setError('Failed to get vessels');
      }
      return [];
    }
  };

  const addLaunchActivity = async (vesselId: string, activityData: any) => {
    try {
      return await customerVesselService.addLaunchActivity(vesselId, activityData);
    } catch (err) {
      console.error('Error adding launch activity:', err);
      setError('Failed to add launch activity');
      throw err;
    }
  };

  const getVesselLaunchActivity = async (vesselId: string) => {
    try {
      return await customerVesselService.getVesselLaunchActivity(vesselId);
    } catch (err) {
      console.error('Error getting vessel launch activity:', err);
      setError('Failed to get launch activity');
      return [];
    }
  };

  const getTodayLaunchActivity = async () => {
    try {
      return await customerVesselService.getTodayLaunchActivity();
    } catch (err) {
      console.error('Error getting today launch activity:', err);
      setError('Failed to get today launch activity');
      return [];
    }
  };

  const value = {
    customers,
    loading,
    error,
    searchCustomers,
    getCustomerById,
    createCustomerVessel,
    updateCustomerVessel,
    deleteCustomerVessel,
    searchVesselsByRegistration,
    searchVesselsByName,
    getAllCustomerVessels,
    addLaunchActivity,
    getVesselLaunchActivity,
    getTodayLaunchActivity,
    refreshCustomers
  };

  return (
    <CustomerVesselContext.Provider value={value}>
      {children}
    </CustomerVesselContext.Provider>
  );
}; 